import { useState, useEffect } from 'react'
import { supabase } from '../api/supabase'
import { useCustomerSearch, type Customer } from '../hooks/useCustomerSearch'

interface JobCharge {
  id: string
  charge_code_id?: string | null
  sale_quantity?: number | null
  sale_rate?: number | null
  sale_amount?: number | null
  sale_vat_rate?: number | null
  charge_codes?: { code: string; description: string } | null
}

interface Job {
  id: string
  job_ref: string
  client_ref?: string
  direction: string
  transport_mode: string
  status: string
  importer_id?: string
  consignor_id?: string
  invoice_to_id?: string
  customer?: Customer
}

interface InvoiceGeneratorProps {
  jobId: string
  onInvoiceCreated?: (invoiceId: string) => void
  onClose?: () => void
}

// Z73.24 follow-up: this used to join job_charges via `!inner` (which hid every job with zero
// charges behind a false "Job not found" -- .single() gets 0 rows back) and read quantity/rate/
// amount/currency/vat_rate, none of which are real job_charges columns (they're sale_quantity/
// sale_rate/sale_amount/sale_currency/sale_vat_rate). See project_charges_phase1 memory.
//
// Also no longer copies selected charges into `invoice_items` -- that table turned out to be the
// Commercial Invoice (customs) line-items table, an entirely different shape (hs_code/
// country_of_origin/net_weight_kg, no charge_code_id or vat_rate at all) that was never actually
// compatible with a sales-charge line. Instead this now does the same thing the Charges tab's own
// Create Invoice does: tag the selected job_charges rows with invoice_id directly, so a charge
// invoiced from here or from the Job's own Charges tab lands in the same place either way.
export default function InvoiceGenerator({ jobId, onInvoiceCreated, onClose }: InvoiceGeneratorProps) {
  const [job, setJob] = useState<Job | null>(null)
  const [charges, setCharges] = useState<JobCharge[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [generating, setGenerating] = useState(false)
  const [generateError, setGenerateError] = useState<string | null>(null)
  const [selectedCharges, setSelectedCharges] = useState<Set<string>>(new Set())
  const [invoiceData, setInvoiceData] = useState({
    customer_id: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    currency: 'GBP',
  })

  const { results: customers } = useCustomerSearch()

  useEffect(() => {
    loadJobData()
  }, [jobId])

  const loadJobData = async () => {
    try {
      setLoading(true)
      setLoadError(null)

      const [jobRes, chargesRes] = await Promise.all([
        supabase
          .from('jobs')
          .select('id, job_ref, client_ref, direction, transport_mode, status, importer_id, consignor_id, invoice_to_id, customer:customers(id, code, name, contact_email)')
          .eq('id', jobId)
          .single(),
        // Only charges not yet on an invoice, and only the Sales side -- a Cost-only line has
        // nothing to bill the customer for.
        supabase
          .from('job_charges')
          .select('id, charge_code_id, sale_quantity, sale_rate, sale_amount, sale_vat_rate, charge_codes(code, description)')
          .eq('job_id', jobId)
          .eq('include_in_sale', true)
          .is('invoice_id', null)
          .order('created_at', { ascending: true }),
      ])

      if (jobRes.error) throw jobRes.error
      if (chargesRes.error) throw chargesRes.error

      setJob(jobRes.data as any)
      setCharges((chargesRes.data as any) || [])

      // Customer only (jobs.invoice_to_id). The importer is never used as a stand-in: an invoice would silently go to the wrong company.
      if (!jobRes.data.invoice_to_id) {
        throw new Error('This job has no Customer yet. Set the Customer in the job header before creating an invoice.')
      }
      setInvoiceData(prev => ({ ...prev, customer_id: jobRes.data.invoice_to_id }))

      setSelectedCharges(new Set((chargesRes.data || []).map((c: any) => c.id)))
    } catch (err) {
      console.error('Error loading job data:', err)
      setLoadError(err instanceof Error ? err.message : 'Failed to load job')
    } finally {
      setLoading(false)
    }
  }

  const calculateTotals = () => {
    const selectedItems = charges.filter(c => selectedCharges.has(c.id))
    const subtotal = selectedItems.reduce((sum, c) => sum + (c.sale_amount || 0), 0)
    const vat = selectedItems.reduce((sum, c) => sum + ((c.sale_amount || 0) * (c.sale_vat_rate || 0) / 100), 0)
    return { subtotal, vat, total: subtotal + vat }
  }

  const handleChargeToggle = (chargeId: string) => {
    setSelectedCharges(prev => {
      const next = new Set(prev)
      next.has(chargeId) ? next.delete(chargeId) : next.add(chargeId)
      return next
    })
  }

  const handleSelectAll = () => setSelectedCharges(new Set(charges.map(c => c.id)))
  const handleDeselectAll = () => setSelectedCharges(new Set())

  const generateInvoice = async () => {
    if (!job || selectedCharges.size === 0 || !invoiceData.customer_id) {
      setGenerateError('Please select charges and a customer.')
      return
    }

    setGenerating(true)
    setGenerateError(null)
    try {
      const { data: invoiceNo, error: noErr } = await supabase.rpc('generate_invoice_no')
      if (noErr || !invoiceNo) throw noErr || new Error('Failed to generate invoice number')

      const totals = calculateTotals()
      const { data: { user } } = await supabase.auth.getUser()

      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .insert({
          invoice_no: invoiceNo,
          job_id: jobId,
          customer_id: invoiceData.customer_id,
          invoice_date: invoiceData.invoice_date,
          due_date: invoiceData.due_date || null,
          currency: invoiceData.currency,
          amount: totals.subtotal,
          vat_amount: totals.vat,
          total_amount: totals.total,
          status: 'ISSUED',
          created_by: user?.email || 'unknown',
        })
        .select('id')
        .single()
      if (invoiceError || !invoice) throw invoiceError || new Error('Failed to create invoice')

      const { error: linkErr } = await supabase
        .from('job_charges')
        .update({ invoice_id: invoice.id })
        .in('id', Array.from(selectedCharges))
      if (linkErr) throw linkErr

      // Real invoicing footprint -- distinct from the job's own lifecycle `status` (a separate
      // short-code workflow, see jobs.status's CHECK constraint), which this deliberately doesn't
      // touch.
      await supabase.from('jobs').update({ is_invoiced: true }).eq('id', jobId)

      onInvoiceCreated?.(invoice.id)
      onClose?.()
    } catch (err) {
      console.error('Error generating invoice:', err)
      setGenerateError(err instanceof Error ? err.message : 'Failed to generate invoice')
    } finally {
      setGenerating(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (loadError || !job) {
    return <div className="p-4 text-red-600">{loadError || 'Job not found'}</div>
  }

  const totals = calculateTotals()

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Generate Invoice</h2>
          <p className="text-gray-600 dark:text-gray-300">Job: {job.job_ref} {job.client_ref && `(${job.client_ref})`}</p>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          ✕
        </button>
      </div>

      {generateError && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm rounded-lg">
          {generateError}
        </div>
      )}

      {/* Invoice Details */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Customer *
          </label>
          <select
            value={invoiceData.customer_id}
            onChange={(e) => setInvoiceData(prev => ({ ...prev, customer_id: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
            required
          >
            <option value="">Select Customer...</option>
            {customers.map((customer: Customer) => (
              <option key={customer.id} value={customer.id}>
                {customer.code} - {customer.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Invoice Date
          </label>
          <input
            type="date"
            value={invoiceData.invoice_date}
            onChange={(e) => setInvoiceData(prev => ({ ...prev, invoice_date: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Due Date
          </label>
          <input
            type="date"
            value={invoiceData.due_date}
            onChange={(e) => setInvoiceData(prev => ({ ...prev, due_date: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Currency
          </label>
          <select
            value={invoiceData.currency}
            onChange={(e) => setInvoiceData(prev => ({ ...prev, currency: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
          >
            {['GBP', 'USD', 'EUR', 'CNY'].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      {/* Charges Selection */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Select Charges</h3>
          <div className="flex gap-2">
            <button
              onClick={handleSelectAll}
              className="px-3 py-1 text-sm bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800"
            >
              Select All
            </button>
            <button
              onClick={handleDeselectAll}
              className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Deselect All
            </button>
          </div>
        </div>

        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={charges.length > 0 && selectedCharges.size === charges.length}
                    onChange={selectedCharges.size === charges.length ? handleDeselectAll : handleSelectAll}
                    className="rounded"
                  />
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Description</th>
                <th className="px-3 py-2 text-right font-medium text-gray-700 dark:text-gray-300">Qty</th>
                <th className="px-3 py-2 text-right font-medium text-gray-700 dark:text-gray-300">Rate</th>
                <th className="px-3 py-2 text-right font-medium text-gray-700 dark:text-gray-300">Amount</th>
                <th className="px-3 py-2 text-right font-medium text-gray-700 dark:text-gray-300">VAT</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {charges.length > 0 ? charges.map(charge => (
                <tr key={charge.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selectedCharges.has(charge.id)}
                      onChange={() => handleChargeToggle(charge.id)}
                      className="rounded"
                    />
                  </td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                    {charge.charge_codes ? `${charge.charge_codes.code} - ${charge.charge_codes.description}` : 'Charge'}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                    {charge.sale_quantity ?? 1}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                    {(charge.sale_rate ?? 0).toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                    {(charge.sale_amount ?? 0).toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                    {charge.sale_vat_rate ?? 0}%
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={6} className="px-3 py-4 text-center text-gray-500 dark:text-gray-400">
                    No uninvoiced sale charges on this job -- add some on its Charges tab first.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Totals */}
      <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg mb-6">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Subtotal</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">
              {totals.subtotal.toFixed(2)} {invoiceData.currency}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">VAT</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">
              {totals.vat.toFixed(2)} {invoiceData.currency}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Total</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">
              {totals.total.toFixed(2)} {invoiceData.currency}
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <button
          onClick={onClose}
          className="px-4 py-2 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          Cancel
        </button>
        <button
          onClick={generateInvoice}
          disabled={generating || selectedCharges.size === 0 || !invoiceData.customer_id}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {generating ? 'Generating...' : 'Generate Invoice'}
        </button>
      </div>
    </div>
  )
}
